import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { respondApi } from '../services/api';
import { parseISO, format } from 'date-fns';
import { DateTimeSelector, AvailabilityDay, SelectedSlot, AvailabilitySlot } from '../components/DateTimeSelector';
import { useEmbedMode } from '../hooks/useEmbedMode';
import { useTheme } from '../hooks/useTheme';
import { Calendar, Clock, Check, Sun, Moon } from 'lucide-react';
import { Footer } from '../components/Footer';

interface EventData {
    id: string;
    name: string;
    comments?: string;
    creator_name: string;
    creator_email: string;
    duration_minutes: number;
    time_slots: {
        id: string;
        start_time_utc: string;
        end_time_utc: string;
    }[];
}

// Common timezones
const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'UTC', label: 'UTC' },
];

export function RespondPage() {
    const { token } = useParams<{ token: string }>();
    const { isEmbedded, notifyParentHeight } = useEmbedMode();
    const { toggleTheme, isDark } = useTheme({ 
        defaultTheme: 'dark',
        storageKey: token 
    });
    const [event, setEvent] = useState<EventData | null>(null);
    const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set());
    const [timezone, setTimezone] = useState('America/New_York');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    
    // Date selection state
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    // Notify parent of height changes
    useEffect(() => {
        notifyParentHeight();
    }, [selectedSlotIds.size, success, notifyParentHeight]);

    useEffect(() => {
        // Try to detect user's timezone
        try {
            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (TIMEZONES.some(tz => tz.value === detected)) {
                setTimezone(detected);
            }
        } catch {
            // Keep default
        }
    }, []);

    useEffect(() => {
        if (!token) return;
        loadEvent();
    }, [token]);

    // Update document title with event name
    useEffect(() => {
        if (event?.name) {
            document.title = `AAM Let's Meet: ${event.name}`;
        }
        return () => {
            document.title = "AAM Let's Meet";
        };
    }, [event?.name]);

    const loadEvent = async () => {
        try {
            const data = await respondApi.getEvent(token!);
            setEvent(data?.event);
            if (data?.recipient?.selected_slot_ids) {
                setSelectedSlotIds(new Set(data.recipient.selected_slot_ids));
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load event');
        } finally {
            setIsLoading(false);
        }
    };

    // Convert event time slots to AvailabilityDay format for DateTimeSelector
    const availableDays = useMemo<AvailabilityDay[]>(() => {
        if (!event) return [];

        // Group slots by date
        const slotsByDate: Record<string, typeof event.time_slots> = {};
        
        event.time_slots.forEach(slot => {
            const date = format(parseISO(slot.start_time_utc), 'yyyy-MM-dd');
            if (!slotsByDate[date]) slotsByDate[date] = [];
            slotsByDate[date].push(slot);
        });

        // Convert to AvailabilityDay format
        return Object.entries(slotsByDate)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, slots]) => {
                const dateObj = parseISO(date);
                const dayName = format(dateObj, 'EEEE');
                
                return {
                    date,
                    day_name: dayName,
                    formatted_date: format(dateObj, 'MMMM d, yyyy'),
                    slots: slots.map(slot => ({
                        start_time_utc: slot.start_time_utc,
                        end_time_utc: slot.end_time_utc,
                        start_time_local: format(parseISO(slot.start_time_utc), 'h:mm a'),
                        end_time_local: format(parseISO(slot.end_time_utc), 'h:mm a'),
                        // Store the slot id for selection tracking
                        id: slot.id
                    }))
                };
            });
    }, [event]);

    // Auto-select first available date
    useEffect(() => {
        if (availableDays.length > 0 && !selectedDate) {
            setSelectedDate(availableDays[0].date);
        }
    }, [availableDays, selectedDate]);

    // Convert selectedSlotIds to SelectedSlot format
    const selectedSlots = useMemo<SelectedSlot[]>(() => {
        if (!event) return [];
        
        const slots: SelectedSlot[] = [];
        availableDays.forEach(day => {
            day.slots.forEach(slot => {
                // Find the original slot by matching times
                const originalSlot = event.time_slots.find(
                    s => s.start_time_utc === slot.start_time_utc && s.end_time_utc === slot.end_time_utc
                );
                
                if (originalSlot && selectedSlotIds.has(originalSlot.id)) {
                    slots.push({
                        ...slot,
                        date: day.date,
                        day_name: day.day_name
                    });
                }
            });
        });
        
        return slots;
    }, [event, availableDays, selectedSlotIds]);

    const handleSlotToggle = (slot: AvailabilitySlot, _date: string, _dayName: string) => {
        if (!event) return;

        // Find the original slot ID
        const originalSlot = event.time_slots.find(
            s => s.start_time_utc === slot.start_time_utc && s.end_time_utc === slot.end_time_utc
        );

        if (!originalSlot) return;

        setSelectedSlotIds(prev => {
            const next = new Set(prev);
            if (next.has(originalSlot.id)) {
                next.delete(originalSlot.id);
            } else {
                next.add(originalSlot.id);
            }
            return next;
        });
        setSuccess(false);
    };

    const handleSubmit = async () => {
        if (!token) return;

        setIsSubmitting(true);
        try {
            await respondApi.submit(token, Array.from(selectedSlotIds));
            setSuccess(true);
        } catch (err: any) {
            setError(err.message || 'Failed to submit response');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="booking-page" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="spinner" />
                </div>
                {!isEmbedded && <Footer />}
            </div>
        );
    }

    if (error && !event) {
        return (
            <div className="booking-page" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-5)'
                }}>
                    <div className="booking-card" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <h2 style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }}>
                            Unable to Load Event
                        </h2>
                        <p style={{ color: '#888' }}>{error}</p>
                    </div>
                </div>
                {!isEmbedded && <Footer />}
            </div>
        );
    }

    if (!event) return null;

    return (
        <div className="booking-page" style={{ 
            paddingBottom: isEmbedded ? 'var(--space-4)' : 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh'
        }}>
            {/* Header - hidden in embed mode */}
            <div className="embed-hide" style={{
                background: 'rgba(255,255,255,0.05)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-6)'
            }}>
                <div className="container" style={{ maxWidth: '900px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: isDark ? 'white' : 'var(--color-navy)' }}>
                            <Calendar size={24} />
                            <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                                AAM <span style={{ color: 'var(--color-primary)' }}>Let's Meet</span>
                            </span>
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container" style={{ maxWidth: '900px', flex: 1 }}>
                {/* Event Info Card */}
                <div className="booking-card" style={{ marginBottom: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                        <h1 style={{ margin: 0 }}>{event.name}</h1>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            style={{ flexShrink: 0 }}
                        >
                            {isDark ? <Sun size={18} /> : <Moon size={18} />}
                        </button>
                    </div>

                    <p style={{
                        color: '#888',
                        marginBottom: 'var(--space-4)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        From <strong style={{ color: isDark ? '#fff' : '#1a1a2e' }}>{event.creator_name}</strong>
                    </p>

                    {event.comments && (
                        <div style={{
                            background: '#2a2a2a',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)',
                            color: '#ccc'
                        }}>
                            {event.comments}
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-4)',
                        color: '#888',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={16} />
                            {event.duration_minutes} minutes
                        </span>
                    </div>
                </div>

                {/* Success Message */}
                {success && (
                    <div style={{ 
                        background: 'rgba(74, 222, 128, 0.1)',
                        border: '1px solid #4ade80',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--space-4)',
                        marginBottom: 'var(--space-4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        color: '#4ade80'
                    }}>
                        <Check size={18} />
                        Your response has been recorded!
                    </div>
                )}

                {/* Instructions */}
                <div className="booking-card" style={{ marginBottom: 'var(--space-4)' }}>
                    <h3 style={{ marginBottom: 'var(--space-2)' }}>
                        Select the times that work for you
                    </h3>
                    <p style={{ color: '#888', fontSize: 'var(--font-size-sm)' }}>
                        Click on any available times that work for your schedule. You can select multiple times.
                    </p>
                </div>

                {/* Date Time Selector */}
                <DateTimeSelector
                    availableDays={availableDays}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    selectedSlots={selectedSlots}
                    onSlotToggle={handleSlotToggle}
                    multiSelect={true}
                    timezone={timezone}
                    onTimezoneChange={setTimezone}
                    showTimezoneSelector={true}
                    ownerName={event.creator_name}
                />

                {/* Submit Section */}
                <div className="booking-card" style={{ marginTop: 'var(--space-4)' }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 'var(--space-3)'
                    }}>
                        <div>
                            <p style={{ color: '#888', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                                {selectedSlotIds.size} time{selectedSlotIds.size !== 1 ? 's' : ''} selected
                            </p>
                        </div>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="btn btn-primary btn-lg"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="spinner" style={{ width: '18px', height: '18px' }} />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Check size={18} />
                                    Submit Response
                                </>
                            )}
                        </button>
                    </div>
                    
                    <p style={{
                        textAlign: 'center',
                        marginTop: 'var(--space-4)',
                        fontSize: 'var(--font-size-sm)',
                        color: '#666'
                    }}>
                        You can return to this page and update your selections anytime
                    </p>
                </div>
            </div>

            {!isEmbedded && <Footer />}
        </div>
    );
}
